import React, { useRef } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const CARDS = [
  {
    id: "sassandra",
    tag: "Ultra Premium · Sassandra",
    title: "Sassandra Integrated Resort",
    body: "1 031 hectares côtiers. Marina 150 places, golf 27 trous, hôtellerie 5 étoiles en joint-venture avec Beachcomber Hotels, villas ultra premium pieds dans l'eau.",
    stat: "1 031 ha",
    accent: "#C49A3C",
    img: "https://images.unsplash.com/photo-1747336755344-54541b22f936?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxjb2FzdGFsJTIwcmVzb3J0JTIwYWVyaWFsfGVufDB8fHx8MTc4MzI3MTgyNXww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "wayne",
    tag: "Réseau multiservices",
    title: "Wayne Multiservices",
    body: "Plus de 100 stations sur le territoire national, dont 28 directement intégrées aux quartiers KIDH. Un hub énergie-commerce-technologie, cœur battant de chaque pôle.",
    stat: "100+ stations",
    accent: "#E8611A",
    img: "https://images.pexels.com/photos/37211263/pexels-photo-37211263.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "gestoci",
    tag: "Infrastructure nationale",
    title: "Infrastructure Pétrolière",
    body: "Partenariat stratégique avec GESTOCI pour le stockage énergétique national. Une infrastructure critique adossée au réseau Wayne et aux quartiers KIDH.",
    stat: "Partenariat GESTOCI",
    accent: "#E8611A",
    img: "https://images.unsplash.com/photo-1746171136129-d59a8bc0507b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxhZnJpY2FuJTIwY2l0eSUyMG1vZGVybiUyMHN0cmVldHxlbnwwfHx8fDE3ODMyNzE4MjV8MA&ixlib=rb-4.1.0&q=85",
  },
];

const Flagship = () => {
  const trackRef = useRef(null);

  const scrollBy = (dir) => {
    if (!trackRef.current) return;
    const width = trackRef.current.clientWidth * 0.75;
    trackRef.current.scrollBy({ left: dir * width, behavior: "smooth" });
  };

  return (
    <section
      id="projets"
      data-testid="flagship-section"
      className="relative bg-[#0A1C33] py-24 md:py-40 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-16 mb-16">
        <div className="flex items-center gap-3 mb-8">
          <span className="kidh-diamond" />
          <span className="font-body text-[0.7rem] uppercase tracking-[0.32em] text-white/60">
            Projets phare
          </span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <h2
            data-testid="flagship-title"
            className="lg:col-span-8 font-heading font-black text-white text-4xl md:text-6xl leading-[0.95] tracking-tighter"
          >
            Trois signatures,<br />
            <span className="text-[#E8611A]">une ambition partagée.</span>
          </h2>
          <div className="lg:col-span-4 flex justify-start lg:justify-end gap-3">
            <button
              onClick={() => scrollBy(-1)}
              className="w-12 h-12 border border-white/25 text-white hover:bg-[#E8611A] hover:border-[#E8611A] transition-colors flex items-center justify-center"
              aria-label="Précédent"
              data-testid="flagship-prev"
            >
              <ArrowRight size={18} className="rotate-180" />
            </button>
            <button
              onClick={() => scrollBy(1)}
              className="w-12 h-12 border border-white/25 text-white hover:bg-[#E8611A] hover:border-[#E8611A] transition-colors flex items-center justify-center"
              aria-label="Suivant"
              data-testid="flagship-next"
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={trackRef}
        className="kidh-carousel flex gap-6 md:gap-10 overflow-x-auto px-6 md:px-16 pb-4"
        data-testid="flagship-carousel"
      >
        {CARDS.map((card, i) => (
          <article
            key={card.id}
            data-testid={`flagship-card-${card.id}`}
            className="group relative flex-none w-[85%] md:w-[70%] lg:w-[62%] aspect-[16/10] overflow-hidden border border-white/10 bg-[#0C2340]"
          >
            <div
              className="absolute inset-0 bg-cover bg-center scale-105 group-hover:scale-110 transition-transform duration-[1600ms] ease-out"
              style={{ backgroundImage: `url(${card.img})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1C33] via-[#0A1C33]/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A1C33]/70 to-transparent" />

            <div className="relative h-full flex flex-col justify-between p-8 md:p-14 text-white">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-3 px-4 py-2 border border-white/25 backdrop-blur-sm">
                  <span
                    className="w-2 h-2 rotate-45"
                    style={{ background: card.accent }}
                  />
                  <span className="font-body text-[0.65rem] uppercase tracking-[0.28em]">
                    {card.tag}
                  </span>
                </div>
                <span className="font-heading font-black text-white/15 text-6xl md:text-8xl tracking-tighter select-none">
                  0{i + 1}
                </span>
              </div>

              <div>
                <div
                  className="font-heading font-black text-3xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tighter mb-4"
                  style={{ color: "#FAFAF8" }}
                >
                  {card.title}
                </div>
                <p className="font-body text-white/85 max-w-xl text-sm md:text-base leading-relaxed mb-8">
                  {card.body}
                </p>
                <div className="flex items-center justify-between gap-6 border-t border-white/20 pt-6">
                  <div>
                    <div className="font-body text-white/50 text-[0.6rem] uppercase tracking-[0.28em]">
                      Ampleur
                    </div>
                    <div
                      className="font-heading font-black text-lg md:text-2xl tracking-tighter"
                      style={{ color: card.accent }}
                    >
                      {card.stat}
                    </div>
                  </div>
                  <a
                    href="#contact"
                    className="group/link inline-flex items-center gap-2 font-body text-[0.7rem] uppercase tracking-[0.24em] font-semibold text-white hover:text-[#E8611A] transition-colors"
                  >
                    En savoir plus
                    <ArrowUpRight
                      size={16}
                      className="group-hover/link:translate-x-1 group-hover/link:-translate-y-1 transition-transform"
                    />
                  </a>
                </div>
              </div>
            </div>
          </article>
        ))}
        <div className="flex-none w-16" />
      </div>
    </section>
  );
};

export default Flagship;
